export interface RoleVisibility {
    id: number | string;
    name: string;
}

export interface Group {
    id: number | string;
    name: string;
    groupColor: string;
}

export class Accounts {
    accountCode: string;
    accountClass: string;
    accountName: string;
    status: string;
    id?: number | string;
    roleVisibility?: RoleVisibility[];
    groups?: Group[];
    accountId: string;
    emoji?: string;
    isInactive?: boolean;
}

export class TaxRates {
    name: string;
    taxType: string;
    rate: number;
    status: string;
}

export class Currencies {
    currencyCode: string;
    description: string;
}

export class TrackingCategories {
    categoryName: string;
    name: string;
    status: string;
}

export class BankingList {
    account: string;
    accountNumber: string;
    currency: string;
    defaultPayment: string;
    assignTo: string;
    bankAccountType: string;
    description: string;
    assignToId: string;
    branchDetails: string;
    accountId: string;
}

/** The types an account can be created with, here and on Settings › Lists › Banking. */
export const NEW_BANKING_ACCOUNT_TYPES = [
    { value: 'BANK', label: 'Bank account', description: 'A transaction or savings account' },
    { value: 'CREDITCARD', label: 'Credit card', description: 'A company or employee credit card' }
];

const ACCOUNT_TYPE_LABELS: { [type: string]: string } = {
    BANK: 'Bank account',
    CREDITCARD: 'Credit card',
    CURRENT: 'Current asset',
    CURRLIAB: 'Current liability',
    EQUITY: 'Equity',
    EXPENSE: 'Expense',
    FIXED: 'Fixed asset',
    INVENTORY: 'Inventory',
    LIABILITY: 'Liability',
    NONCURRENT: 'Non-current asset',
    PREPAYMENT: 'Prepayment',
    REVENUE: 'Revenue',
    SALES: 'Sales',
    TERMLIAB: 'Non-current liability'
};

/** Xero codes ("CREDITCARD") and QuickBooks names ("Credit Card") compare the same. */
function normaliseAccountType(type: string): string {
    return (type || '').replace(/[\s_-]/g, '').toUpperCase();
}

export function isCreditCardAccountType(type: string): boolean {
    return normaliseAccountType(type) === 'CREDITCARD';
}

export function isBankAccountType(type: string): boolean {
    return normaliseAccountType(type) === 'BANK';
}

/**
 * A readable account type or class: "CURRLIAB" is "Current liability". The Banking
 * list includes any account payments can be made to, not only bank accounts.
 */
export function accountTypeLabel(type: string): string {
    const known = ACCOUNT_TYPE_LABELS[normaliseAccountType(type)];
    if (known) {
        return known;
    }
    const text = (type || '').trim();
    return text ? text.charAt(0).toUpperCase() + text.slice(1).toLowerCase() : '';
}
